import React, { useState } from 'react';
import { TableCell, TableRow } from 'src/components/ui/table';
import { Invoice, InvoiceLineItem, SKU } from '../../../../redux/billing/billing.types';
import InvoiceDetailButton from '../invoice-detail-button';
import { Antenna, Check, CheckCheck, DollarSign, Eye, EyeOff, Flag, Hash, Move, Pen, Percent, Play, RotateCcw, Tag, View } from 'lucide-react';
import TextIconInput from '../text-icon-input';
import { Button } from 'src/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { InvoiceLineItemDetailCalc } from './invoice-line-item-detail-calc';
import { getCompanyName } from './utils';
import { Company } from 'src/pages/billing/types/billing.types';

interface LineItemDetailsCardProps {
  isLastRow: boolean;
  lineItem: InvoiceLineItem;
  invoice: Invoice;
  invoices: Invoice[];
  invoiceIndex: number;
  platforms: string[];
  skus: SKU[];
  sortedSKUs: Array<{ id: string; name: string; sku: string; bill_type: string | any }>;
  textFieldValues: Record<string, string>;
  companyLookup: Record<string, Company>;
  updateTextFieldValue: (fieldId: string, value: string) => void;
  updateLineItemField: (lineItemId: string, field: string, value: any) => void;
  getTextFieldValue: (fieldId: string, defaultValue: string) => string;
}

export const LineItemDetailsCard: React.FC<LineItemDetailsCardProps> = ({
  isLastRow,
  lineItem,
  invoice,
  invoices,
  invoiceIndex,
  platforms,
  skus,
  sortedSKUs,
  textFieldValues,
  companyLookup,
  updateTextFieldValue,
  updateLineItemField,
  getTextFieldValue,
}) => {
  const [moveSearchQuery, setMoveSearchQuery] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState(invoices.slice().sort((a, b) => getCompanyName(a.company, companyLookup, [a]).localeCompare(getCompanyName(b.company, companyLookup, [b]))));

  const handleSearch = (query: string) => {
    setMoveSearchQuery(query);
    if (query === '') {
      setFilteredInvoices(invoices.slice().sort((a, b) => getCompanyName(a.company, companyLookup, [a]).localeCompare(getCompanyName(b.company, companyLookup, [b]))));
    } else {
      setFilteredInvoices(invoices.filter((invoice) => getCompanyName(invoice.company, companyLookup, [invoice]).toLowerCase().includes(query.toLowerCase())));
    }
  };

  const useOverrideValue = (field: keyof InvoiceLineItem) => {
    const overrideField = `${field}_override` as keyof InvoiceLineItem;
    const overrideValue = lineItem[overrideField];
    return overrideValue !== null && overrideValue !== undefined ? overrideValue : lineItem[field];
  };

  const getSkuName = (sku: { id: string; name: string; sku: string; bill_type: string }) => {
    return (
      <div className='flex flex-col items-start justify-start text-left pb-1'>
        <div className='text-sm font-bold text-gray-700'>
          {`${sku.name} (${sku.sku})`}
        </div>
        <div className='text-xs font-light text-gray-500'>
          {sku.bill_type}
        </div>
      </div>
    );
  };

  return (
    <TableRow
      className={`border-yellow-500 border-l-4 border-r-4 bg-gray-201 ${isLastRow ? 'border-b-4 border-b-yellow-500' : 'border-b-gray-400 border-b-1'}`}
    >
      <TableCell
        colSpan={6}
        className="p-0"
      >
        <div className="">
          <div className='flex flex-row border-b border-gray-400 justify-between'>
            <div className='flex flex-row gap-1 items-center ml-2'>
              <InvoiceLineItemDetailCalc
                lineItem={lineItem}
                sku={lineItem.sku_override ? skus.find((sku) => sku.id === lineItem.sku_override)! : skus.find((sku) => sku.id === lineItem.sku)!}
              />
            </div>
            <div className='flex flex-row'>
              {/* TODO add reset endpoint */}
              <Button
                title='Set back to original values'
                size='med'
                variant='ghost'
                onClick={() => updateLineItemField(lineItem.id, 'reset', true)}
                className='relative flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-l border-gray-400 hover:bg-gray-400'
              >
                <RotateCcw className='h-4 w-4' />
                Reset
              </Button>
              <Button
                title={lineItem.invoice_line_item_status === 'approved' ? 'Set back to draft' : 'Approve'}
                size='med'
                variant='ghost'
                onClick={() => updateLineItemField(lineItem.id, 'invoice_line_item_status', lineItem.invoice_line_item_status === 'approved' ? 'draft' : 'approved')}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-400 hover:bg-gray-400 ${lineItem.invoice_line_item_status === 'approved' ? 'bg-green-501 text-white border-green-501 hover:bg-green-500 hover:border-green-500' : 'bg-transparent'}`}
              >
                {lineItem.invoice_line_item_status === 'approved' ? <CheckCheck className={`h-4 w-4 text-white`} /> : <Check className={`h-4 w-4 text-black`} />}
                {lineItem.invoice_line_item_status === 'approved' ? 'Approved' : 'Approve'}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size='med'
                    variant='ghost'
                    className='relative flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-400 hover:bg-gray-400'
                  >
                    <Move className='h-4 w-4' />
                    Move
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='start'
                  side='bottom'
                  className='w-fit !max-h-[300px] !overflow-y-auto z-50'
                >
                  <div>
                    <input
                      type="text"
                      placeholder="Search companies"
                      className="w-full text-sm h-8 border-b border-gray-400 text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                      value={moveSearchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredInvoices.map((invoice) => (
                    <DropdownMenuItem
                      key={invoice.id}
                      onClick={() => updateLineItemField(lineItem.id, 'invoice', invoice.id)}
                    >
                      <div className='flex flex-col text-sm font-bold text-gray-700'>
                        {getCompanyName(invoice.company, companyLookup, [invoice])}
                        <span className='text-xs font-light text-gray-500'>
                          {invoice.invoice_line_items.length}
                          {' '}
                          line item
                          {invoice.invoice_line_items.length !== 1 ? 's' : ''}
                        </span>
                      </div>


                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* <Button
                size='med'
                variant='ghost'
                onClick={() => updateLineItemField(lineItem.id, 'do_not_bill', !lineItem.do_not_bill)}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-400 hover:bg-gray-400 ${lineItem.do_not_bill ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-300 hover:border-orange-300' : 'bg-transparent'}`}
              >
                <CircleOff className={`h-4 w-4 ${lineItem.do_not_bill ? 'text-white' : 'text-black'}`} />
                Do Not Bill
              </Button> */}
              <Button
                size='med'
                variant='ghost'
                onClick={() => updateLineItemField(lineItem.id, 'hide', !lineItem.hide)}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-400 hover:bg-gray-400 ${lineItem.hide ? 'bg-gray-700 text-white border-gray-700 hover:border-gray-400 hover:text-black' : 'bg-transparent'}`}
              >
                {lineItem.hide ? <Eye className='h-4 w-4' /> : <EyeOff className='h-4 w-4' />}
                {lineItem.hide ? 'Show' : 'Hide'}
              </Button>

              <Button
                size='med'
                variant='ghost'
                onClick={() => updateLineItemField(lineItem.id, 'attention_needed', !lineItem.attention_needed)}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-gray-400 hover:bg-gray-400 ${lineItem.attention_needed ? 'bg-red-501 text-white hover:bg-red-400' : 'bg-transparent'}`}
              >
                <Flag className={`h-4 w-4 ${lineItem.attention_needed ? 'text-white' : 'text-black'}`} />
                {lineItem.attention_needed ? 'Flagged' : 'Flag'}
              </Button>
            </div>
          </div>

          <div className='grid grid-cols-8 border-b border-gray-400'>
            <div className='flex flex-row w-1/3 col-span-2'>
              <div className='flex flex-row pr-2 mb-2'>
                <div className='col-span-2 p-2'>
                  <div className='text-black font-bold flex text-md items-center gap-2 justify-between'>
                    <div className='flex items-center gap-1 min-w-[150px]'>
                      <Hash className='h-4 w-4 ' />
                      {lineItem.ext_line_item_id || 'Not Assigned'}
                    </div>
                    {/* <StatusBadgeThin status={invoice.invoice_status} /> */}
                  </div>
                  <div className='text-xs font-light text-gray-500 flex items-center gap-1 ml-1 min-w-[100px]'>
                    <div className={`w-2 h-2 ${lineItem.validated ? 'bg-green-501' : 'bg-red-501'} rounded-full no-wrap`} />
                    {/* TODO replace with verified when implemented */}
                    {lineItem.validated ? 'Verified' : 'Not Verified'}
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-row col-span-2' />

            <div className='flex flex-row col-span-4 grid grid-cols-6'>
              <InvoiceDetailButton
                label={'Platform'}
                icon={<Play className='h-3 w-3 text-black mr-1' />}
                color={'bg-transparent text-black border-l border-r border-gray-400 p-1 hover:bg-gray-400'}
                textColor={'text-black'}
                isDropdown={true}
                value={lineItem.platform || ''}
                options={platforms.map((platform) => ({
                  id: platform,
                  name: platform,
                }))}
                onSelect={(e) => updateLineItemField(lineItem.id, 'platform', e)}
              />
              <InvoiceDetailButton
                label={'Channel'}
                icon={<Antenna className='h-3 w-3 text-black mr-1' />}
                color={'bg-transparent text-black border-l border-r border-gray-400 p-1 hover:bg-gray-400'}
                textColor={'text-black'}
                isDropdown={true}
                value={lineItem.channel || ''}
                options={['programmatic', 'search', 'social'].map((channel) => ({
                  id: channel,
                  name: channel,
                }))}
                onSelect={(e) => updateLineItemField(lineItem.id, 'channel', e)}
              />
              <InvoiceDetailButton
                label={'SKU'}
                icon={<Tag className='h-3 w-3 text-black mr-1' />}
                color={'bg-transparent text-black border-l border-r border-gray-400 p-1 hover:bg-gray-400'}
                textColor={'text-black'}
                isDropdown={true}
                useSearch={true}
                value={sortedSKUs.find((sku) => sku.id === (lineItem.sku_override || lineItem.sku))?.name || ''}
                options={sortedSKUs.map((sku) => ({
                  id: sku.id,
                  name: sku.name,
                  child: getSkuName(sku),
                }))}
                onSelect={(e) => updateLineItemField(lineItem.id, 'sku_override', e)}
              />
            </div>

          </div>
          <div className='grid grid-cols-12'>

            <div className='col-span-2 border-r border-gray-400 hover:bg-gray-400'>
              <TextIconInput
                icon={<View className='h-3 w-3 text-black mr-1' />}
                label="Quantity"
                value={useOverrideValue('qty') != null ? Number(useOverrideValue('qty')).toFixed(0) : '0'}
                onChange={(e) => {updateLineItemField(lineItem.id, 'qty_override', parseInt(e));}}
              />
            </div>
            <div className='col-span-2 border-r border-gray-400 hover:bg-gray-400'>
              <TextIconInput
                icon={<DollarSign className='h-3 w-3 text-black mr-1' />}
                label="Spend"
                value={useOverrideValue('advertiser_spend') != null ? Number(useOverrideValue('advertiser_spend')).toFixed(2) : '0.00'}
                onChange={(e) => {updateLineItemField(lineItem.id, 'advertiser_spend_override', parseFloat(e));}}
              />
            </div>
            <div className='col-span-2 border-r border-gray-400 hover:bg-gray-400'>
              <TextIconInput
                icon={<DollarSign className='h-3 w-3 text-black mr-1' />}
                label="Margin (cannot edit)"
                value={lineItem.margin?.toFixed(2) || '0.00'}
                onChange={() => {}}
                disabled={true}
              />
            </div>
            <div className='col-span-2 border-r border-gray-400 hover:bg-gray-400'>
              <TextIconInput
                icon={<Percent className='h-3 w-3 text-black mr-1' />}
                label="Margin Percent"
                inputClassName='text-right p-2'
                value={(() => {
                  const marginPct = useOverrideValue('margin_pct');
                  if (marginPct === null || marginPct === undefined) {
                    return '';
                  }
                  return (Number(marginPct) * 100).toFixed(0);
                })()}
                onChange={(e) => {
                  const sanitized = e.replace(/[^\d]/g, '').slice(0, 2);
                  const valueToSend = sanitized === '' ? '0' : sanitized;
                  updateLineItemField(lineItem.id, 'margin_pct_override', valueToSend);
                }}
              />
            </div>
            <div className='col-span-4 border-r border-gray-400 w-full hover:bg-gray-400'>
              <TextIconInput
                icon={<Pen className='h-3 w-3 text-black mr-1' />}
                label="Notes"
                inputClassName='!w-full !min-w-full text-left'
                inputOnly={true}
                value={lineItem.notes || ''}
                onChange={(e) => { updateLineItemField(lineItem.id, 'notes', e); }}
              />
            </div>
          </div>


        </div>
      </TableCell>
    </TableRow>
  );
};

export default LineItemDetailsCard;
